import { api } from '@/lib/api';
import { Appointment, AppointmentStatus, Surgery, SurgicalStatus, AppointmentDateQuery, AppointmentAttachType, VisitType, ApiResponse } from '@/types/types';

// Upload attachment interface for API calls
interface UploadAttachmentData {
    file: File;
    type: AppointmentAttachType;
    appointmentId: string;
}

export const appointmentApi = {
    // Get appointment by ID
    getAppointmentById: (id: string) => api.get(`/api/appointment/${id}`),

    // Get patient appointment history
    getPatientHistory: () => api.get('/api/appointments/history'),

    /** Hospitals the user may book at (multi-site chain). */
    getBookingHospitals: () =>
        api
            .get<ApiResponse<{ id: string; name: string }[]>>('/api/appointment/booking-hospitals')
            .then((res) => res.data.data ?? []),

    // Book a new appointment
    bookAppointment: (appointment: Partial<Appointment>) =>
        api.post('/api/appointment/book', appointment),

    // Upload attachment for appointment 
    uploadAttachment: (data: UploadAttachmentData) => {
        const formData = new FormData();
        formData.append('file', data.file);
        formData.append('type', data.type);
        return api.post(`/api/appointment/upload-attachment?id=${data.appointmentId}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    // Get attachments for appointment
    getAttachments: (id: string) =>
        api.get(`/api/appointment/get-attachments/${id}`),

    // Delete attachment
    deleteAttachment: (id: string) =>
        api.delete(`/api/appointment/delete-attachment/${id}`),
 
    // Update appointment status
    updateAppointmentStatus: (id: string, status: AppointmentStatus) =>
        api.patch(`/api/appointment/update-status/${id}`, { status }),

    /** Reception confirms CRM free OPD — auto zero bill + queue. */
    confirmFreeCrmOpd: (appointmentId: string) =>
        api.post<ApiResponse<{ appointmentId: string; billId: string | null }>>(
            `/api/appointment/${appointmentId}/confirm-free-opd`
        ),

    // Get appointments by date and doctor
    getAppointmentsByDateAndDoctor: (query: AppointmentDateQuery) =>
        api.get('/api/appointment/get-by-date-and-doctor', { params: query }),

    // Get appointments by hospital
    getAppointmentsByHospital: () =>
        api.get<ApiResponse<Appointment[]>>('/api/appointment/get-by-hospital'),

    getAppointmentsByHospitalAndVisitType: (visitType: VisitType) =>
        api.get(`/api/appointment/get-by-hospital?visitType=${visitType}`),
 
    // Get appointments by date and patient
    getAppointmentsByDateAndPatient: (query: AppointmentDateQuery) =>
        api.get('/api/appointment/get-by-date-and-patient', { params: query }),

    // Get appointments by date
    getAppointmentsByDate: (query: AppointmentDateQuery) =>
        api.get('/api/appointment/get-by-date', { params: query }),

    /** Today (IST) and all future appointments, ascending by scheduledAt. */
    getUpcomingAppointments: () =>
        api.get<ApiResponse<Appointment[]>>('/api/appointment/get-upcoming'),

    /** Search by patient name, phone, visit id, or doctor (all dates, newest first). */
    searchAppointments: (q: string, limit = 100) =>
        api.get<ApiResponse<Appointment[]>>('/api/appointment/search', {
            params: { q, limit },
        }),

    // Get created appointments
    getCreatedAppointments: () =>
        api.get<ApiResponse<Appointment[]>>('/api/appointment/get-created-appointments'),

    // Get created appointments by date
    getCreatedAppointmentsByDate: (date: string) =>
        api.get<ApiResponse<Appointment[]>>('/api/appointment/get-created-appointments-by-date', {
            params: { date }
        }),

    // Get surgery by appointment ID
    getSurgeryByAppointmentId: (appointmentId: string) => {
        if (!appointmentId) {
            console.error('appointmentId is required but not provided');
            return Promise.reject(new Error('appointmentId is required'));
        }
        return api.get(`/api/appointment/get-surgery-by-appointment-id/${appointmentId}`);
    },



    // Add surgery to appointment
    addSurgery: (surgery: Partial<Surgery>) =>
        api.post('/api/appointment/add-surgery', surgery),

    // Update surgery status
    updateSurgeryStatus: (surgeryId: string, status: SurgicalStatus, scheduledAt: string) =>
        api.patch(`/api/appointment/update-surgery-status/${surgeryId}`, { status, scheduledAt }),

    // Get surgeries by hospital
    getSurgeryByHospitalId: () =>
        api.get('/api/appointment/get-surgery-by-hospital-id'),

    // Update appointment schedule (doctor, hospital, visit type, registration source)
    updateAppointmentSchedule: (
        id: string,
        payload: {
            scheduledAt: string | Date;
            doctorId?: string;
            hospitalId?: string;
            visitType?: string;
            registrationSource?: string;
            registrationSourceDetails?: string;
        }
    ) => api.patch(`/api/appointment/update-appointment-schedule/${id}`, payload),

    getOpdPayoutContext: (appointmentId: string) =>
        api.get<
            ApiResponse<{
                appointmentId: string;
                visitId: string | null;
                eligible: boolean;
                reason?: string;
                opdConsultNet: number;
                billDate: string | null;
                serviceMonth: string | null;
                doctor: { id: string; name: string } | null;
                existingExpense: {
                    id: string;
                    expenseNumber: string | null;
                    amount: number;
                    workflowStatus: string;
                } | null;
            }>
        >(`/api/appointment/${appointmentId}/opd-payout`),

    createOpdPayout: (
        appointmentId: string,
        body: {
            amount: number;
            description?: string;
            serviceMonth?: string;
            expenseDate?: string;
            paymentMode?: string;
        }
    ) =>
        api.post<
            ApiResponse<{
                id: string;
                expenseNumber: string | null;
                amount: number;
                workflowStatus: string;
            }>
        >(`/api/appointment/${appointmentId}/opd-payout`, body),
};
